// hooks/useIssueFromSuggestion.ts
import { useCallback } from 'react';
import api from 'config/axiosConfig';
import { Issue } from 'util/constants';
import { standardizeWikipediaUrl } from 'util/posts';
import { createIssueFromWikipedia } from 'util/issueCreationUtils';
import { WikiSuggestion } from 'hooks/useIssueSearch';
import { apiRoutes } from "util/apiRoutes";

// If you already have this elsewhere, keep a single copy and import it instead.

type SuggestionLike = {
  type?: string;           // 'issue' | 'wikiSuggestion' | etc.
  id?: number | string;
  title?: string;          // wikipedia title or our UI label
  name?: string;           // sometimes suggestions come with `name`
  cname?: string;          // canonical name
  wikipedia_link?: string; // ideal field
  url?: string;            // fallback
  link?: string;           // fallback
  pageid?: number;         // nice-to-have
  properties?: Record<string, any>;
  subject_properties?: Record<string, any>; // wiki properties
};

// Global in-memory cache to dedupe concurrent ensure calls across components
// Keyed by canonical cname or standardized wikipedia link
// Value is a Promise<Issue> so concurrent callers await the same work
const __globalIssueEnsureCache: Map<string, Promise<Issue>> = ((): Map<string, Promise<Issue>> => {
  const g = globalThis as unknown as { __issueEnsureCache?: Map<string, Promise<Issue>> };
  if (!g.__issueEnsureCache) {
    g.__issueEnsureCache = new Map<string, Promise<Issue>>();
  }
  return g.__issueEnsureCache;
})();

export function useIssueFromSuggestion() {

  const ensureIssue = useCallback(async (entity: SuggestionLike | Issue | null): Promise<Issue | null> => {
    if (!entity) return null;

    // Already a real Issue — just return it
    if ((entity as { type?: string }).type === 'issue' ||
        ((entity as { id?: number; cname?: string; wikipedia_link?: string }).id !== undefined &&
         (entity as { cname?: string }).cname &&
         (entity as { wikipedia_link?: string }).wikipedia_link)) {
      return entity as Issue;
    }

    // Suggestion -> ensure or create Issue idempotently
    const s = entity as SuggestionLike;
    const canonicalNameRaw = (s.cname || s.title || s.name || '').trim();
    const canonicalName = canonicalNameRaw.toLowerCase();
    const wikipediaRaw = s.wikipedia_link || s.link || s.url || s.properties?.wikipedia_link || null;
    const standardizedWiki = standardizeWikipediaUrl(wikipediaRaw || '');

    const cacheKeys = new Set<string>();
    if (canonicalName) cacheKeys.add(`cname:${canonicalName}`);
    if (standardizedWiki) cacheKeys.add(`wiki:${standardizedWiki.toLowerCase()}`);

    // Return cached promise if available
    for (const k of Array.from(cacheKeys)) {
      const cached = __globalIssueEnsureCache.get(k);
      if (cached) return cached;
    }

    const ensurePromise = (async (): Promise<Issue> => {
      // 1) Try to fetch by canonical title first (fast path / idempotent)
      if (canonicalName) {
        try {
          const existingByName = await api.get(apiRoutes.issues.byName(encodeURIComponent(canonicalNameRaw)));
          if (existingByName.status === 200 && (existingByName.data?.id as number | undefined)) {
            return existingByName.data as Issue;
          }
        } catch {
          // not found, continue to creation path
        }
      }

      // 2) Create if not found
      // Build a WikiSuggestion object compatible with our centralized utility
      const wikiSuggestion: WikiSuggestion = {
        type: 'wiki' as const,
        name: s.name || canonicalNameRaw,
        link: standardizedWiki || '',
        thumbnail: (s.subject_properties as any)?.thumbnail?.source || '',
        cname: canonicalNameRaw,
        subject_properties: s.subject_properties || {},
        short_description: (s.subject_properties as any)?.description || '',
      };

      let created: Issue | null = null;
      try {
        created = await createIssueFromWikipedia(wikiSuggestion);
      } catch (err: unknown) {
        // If server reports conflict, fetch existing by name and return it
        const maybeAxios = err as { response?: { status?: number } };
        if (maybeAxios.response?.status === 409 && canonicalName) {
          const existing = await api.get(apiRoutes.issues.byName(encodeURIComponent(canonicalNameRaw)));
          if (existing.status === 200) {
            created = existing.data as Issue;
          }
        } else {
          throw err;
        }
      }

      if (!created) {
        throw new Error('Failed to create or retrieve Issue');
      }

      return created;
    })();

    // Populate all keys with the same promise; remove on rejection
    Array.from(cacheKeys).forEach((k) => { __globalIssueEnsureCache.set(k, ensurePromise); });
    try {
      const result = await ensurePromise;
      return result;
    } catch (err) {
      Array.from(cacheKeys).forEach((k) => { __globalIssueEnsureCache.delete(k); });
      throw err;
    }
  }, []);

  return { ensureIssue };
}
